import { Button, Card, Grid, Input, InputLabel, MenuItem, Select, Theme, Typography } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import dayjs from 'dayjs';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import CurrencyFormat from 'src/components/currency/Currency';
import Loading from 'src/components/loading/Loading';
import WarningBox from 'src/components/WarningBox';
import RootStore from 'src/stores/RootStore';
import UserStore from 'src/stores/UserStore';
import { OrderMode } from 'src/utils/constants';
import { isWeekday } from 'src/utils/deliveryDateMaths';
import EarliestShippingDate from './EarliestShippingDate';
import DeliveryDatePicker from './DeliveryDatePicker';
import Toast from 'src/services/Toasts';

interface CartDetailsInclusiveProps extends WithStyles<typeof styles> {
    store?: typeof RootStore.Type;
    user?: typeof UserStore.Type;
}

const CartDetailsInclusive = inject(
    'store',
    'user',
)(
    observer(({ classes, store, user }: CartDetailsInclusiveProps) => {
        const navigate = useNavigate();
        const today = dayjs();
        const orderCommentMaxLength = 30;
        const earliestShippingDate = store?.cart.earliestShippingDate!;

        const [orderComment, setOrderComment] = useState(
            user?.impersonate !== null
                ? user!.name
                      .replace(/[^\w\.\-\+].*$/, '')
                      .substring(0, orderCommentMaxLength)
                      .toUpperCase()
                : '',
        );
        const [isSubmitting, setIsSubmitting] = useState(false);

        const language = user && user?.preferredLang !== 'en-GB' ? user?.preferredLang.substring(0, 2) : 'en';

        const isCartEmpty = !store!.cart.items.length;
        const isSubmitDisabled = isCartEmpty || store!.cart.bundleWarning;

        useEffect(() => {
            // Request quote on cart open
            store!.cart.getOrderQuote();
        }, []);

        const handleOrderSubmit = useCallback(() => {
            if (!user?.canCreateOrders) {
                Toast.CreateOrderProblemWithAccount();
                return;
            }

            setIsSubmitting(true);
            store!.cart
                .placeOrderInclusive(store!.cart.requestedDeliveryDate, navigate, store!.cart.orderMode, orderComment)
                .then(() => {
                    setIsSubmitting(false);
                });
        }, [store!.cart.requestedDeliveryDate, store!.cart.orderMode, orderComment]);

        const handleOnChangeDiliveryDate = useCallback(async (date: Date) => {
            store!.cart.setRequestedDeliveryDate(date);
            await store!.cart.getOrderQuote();
        }, []);

        const handleSelectOrderMode = useCallback(async e => {
            store!.cart.setOrderMode(e.target.value as OrderMode);
            await store!.cart.getOrderQuote();
        }, []);

        if (!store!.cart.isLoaded) {
            return <Loading small />;
        }

        return (
            <Card className={classes.card}>
                {isSubmitting ? (
                    <Grid
                        container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <>
                            <Typography>
                                <FormattedMessage id="cart.placing_order" />
                            </Typography>
                            <Loading />
                        </>
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item sm={12}>
                            <div className={classes.earliestShippingDate}>
                                {!isCartEmpty && (
                                    <EarliestShippingDate
                                        earliestShippingDate={earliestShippingDate.toISOString()}
                                        today={today}
                                        totalBikes={store!.cart.totalBikesInCart}
                                    />
                                )}
                            </div>
                        </Grid>

                        <Grid item sm={6}>
                            <DeliveryDatePicker
                                today={today}
                                minDate={earliestShippingDate}
                                selectedDate={store!.cart.requestedDeliveryDate}
                                setSelectedDate={handleOnChangeDiliveryDate}
                                language={language}
                                filterDate={(date: Date) => isWeekday(date, [0, 6])}
                            />
                        </Grid>

                        <Grid item sm={6}>
                            <>
                                <Typography align="right">
                                    <FormattedMessage id="cart.total" />
                                </Typography>

                                <Typography variant={'h6'} align="right">
                                    <CurrencyFormat value={store!.cart.orderQuotePrice} />
                                </Typography>
                            </>
                        </Grid>

                        <Grid item sm={12} className={classes.buttonWrapper}>
                            <div data-testid="bundled-bikes-message" className={classes.messageWrapper}>
                                {store!.cart.bundleWarning && store!.cart.totalBikesInCartInclusive > 1 ? (
                                    <WarningBox
                                        messageKey="warning.bundledBikes"
                                        titleKey="warning.title"
                                        closeBundleWarning={() => store!.cart.toggleBundleWarning(false)}
                                    />
                                ) : (
                                    ''
                                )}
                            </div>

                            {user?.impersonate !== null && (
                                <div>
                                    <InputLabel htmlFor="order-comment-input">
                                        <FormattedMessage id="impersonate.comment" />
                                    </InputLabel>
                                    <Input
                                        data-testid="order-comment-input"
                                        id="order-comment"
                                        value={orderComment}
                                        onChange={e =>
                                            setOrderComment(
                                                e.target.value.substring(0, orderCommentMaxLength).toUpperCase(),
                                            )
                                        }
                                        fullWidth
                                        sx={{ height: '2rem', marginBottom: '1rem' }}
                                    />
                                    <Select
                                        data-testid="orderModeSelector"
                                        labelId="order-status"
                                        value={store!.cart.orderMode}
                                        onChange={handleSelectOrderMode}
                                        fullWidth
                                        sx={{ height: '2rem', marginBottom: '1rem' }}
                                    >
                                        {Object.values(OrderMode).map(mode => (
                                            <MenuItem
                                                key={mode}
                                                value={mode}
                                                data-testid={`order-status-${mode.toLowerCase().replace(' ', '-')}`}
                                            >
                                                {mode}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            <Button
                                data-testid="submit-order"
                                disabled={isSubmitDisabled || isSubmitting}
                                variant="contained"
                                color="primary"
                                onClick={handleOrderSubmit}
                                fullWidth
                            >
                                <FormattedMessage id="cart.place_order" />
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Card>
        );
    }),
);

function styles({ breakpoints, palette }: Theme) {
    return createStyles({
        card: {
            flexGrow: 1,
            maxWidth: '100%',
            margin: '0 0 1.5%',
            padding: '0',
            boxShadow: 'none',
            [breakpoints.down('sm')]: {
                maxWidth: '100%',
                margin: '0 0 10%',
            },
            overflow: 'visible',
        },
        earliestShippingDate: {
            marginBottom: '1rem',
        },
        buttonWrapper: {
            position: 'relative',
        },
        messageWrapper: {
            position: 'absolute',
            bottom: '100%',
            left: 24,
            zIndex: 1,
        },
    });
}

export default withStyles(styles)(CartDetailsInclusive);
